import { useState, useEffect, useCallback } from 'react';
import { Box, Card, CardContent, CardHeader, Typography, Button, TextField, Switch, FormControlLabel, Grid, Divider, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip } from '@mui/material';
import toast from 'react-hot-toast';
import { attendanceApi } from '@/api/attendance.api';
import useSchoolStore from '@/store/schoolStore';
import { PageHeader } from '@/components/navigation/PageHeader';
import { PATHS } from '@/router/paths';
import { usePageMeta } from '@/hooks/usePageMeta';
import { RefreshCw, Save, Download, Copy, Check, Settings as SettingsIcon, QrCode } from 'lucide-react';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';

export function AttendanceSettingsPage() {
  usePageMeta('Settings | Attendance', [
    { label: 'Attendance', path: '/attendance' },
    { label: 'Settings' },
  ]);

  const { currentSchool } = useSchoolStore();
  const [settings, setSettings] = useState({
    attendance_mode: 'QR',
    qr_type: 'STATIC',
    qr_refresh_interval: 0,
    checkin_start_time: '05:00:00',
    late_after: '09:15:00',
    half_day_after: '11:00:00',
    checkout_after: '17:00:00',
    working_hours: 8,
    auto_mark_absent: true,
    is_active: true,
    qr_secret: '',
  });
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const [qrData, setQrData] = useState(null);

  const fetchSettings = useCallback(async () => {
    if (!currentSchool?.id) return;
    try {
      const data = await attendanceApi.getSettings(currentSchool.id);
      if (data) {
        setSettings({ ...data, qr_refresh_interval: 0, qr_type: data.qr_type || 'STATIC' });
      }
    } catch (error) {
      console.error('Failed to fetch attendance settings', error);
    }
  }, [currentSchool]);

  const fetchQrData = useCallback(async () => {
    if (!currentSchool?.id) return;
    try {
      const data = await attendanceApi.getQrData(currentSchool.id);
      if (data) {
        setQrData(data);
      }
    } catch (error) {
      console.error('Failed to fetch QR data', error);
    }
  }, [currentSchool]);

  useEffect(() => {
    if (currentSchool?.id) {
      fetchSettings();
      fetchQrData();
    }
  }, [currentSchool, fetchSettings, fetchQrData]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const payload = { ...settings, qr_refresh_interval: 0 };
      const res = await attendanceApi.updateSettings(currentSchool.id, payload);
      if (res) {
        toast.success('Attendance settings updated successfully.');
        fetchQrData();
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateQR = async () => {
    try {
      setLoading(true);
      const res = await attendanceApi.regenerateQrSecret(currentSchool.id);
      if (res) {
        toast.success('QR secret regenerated.');
        fetchSettings();
        fetchQrData();
      }
    } catch (error) {
      console.error('Regenerate QR error:', error);
      toast.error('Failed to regenerate QR');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = () => {
    const qrElement = document.getElementById('qr-code-container');
    if (qrElement) {
      html2canvas(qrElement).then((canvas) => {
        const link = document.createElement('a');
        link.download = 'Attendance_QR_Code.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      });
    }
  };

  const handleCopySecret = () => {
    if (settings?.qr_secret) {
      navigator.clipboard.writeText(settings.qr_secret);
      setCopied(true);
      toast.success('QR secret copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Attendance Settings"
        subtitle="Configure attendance rules, QR modes, and time cut-offs"
        backPath={PATHS.SETTINGS}
        action={
          <Button
            variant="contained"
            startIcon={<Save size={18} />}
            onClick={handleSave}
            disabled={loading}
          >
            Save Settings
          </Button>
        }
      />

      <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
        {/* Left Card: All Settings & Time Rules */}
        <Grid size={{ xs: 12, md: 7 }} sx={{ display: 'flex' }}>
          <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', flex: 1 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SettingsIcon size={20} />
                  <Typography variant="h6" fontWeight={600}>General & Time Rules</Typography>
                </Box>
              }
              subheader="Configure attendance tracking mode and daily timing rules"
            />
            <Divider />
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 3, flex: 1, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* General Settings Section */}
                <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  General Mode & Hours
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Attendance Mode</InputLabel>
                      <Select
                        label="Attendance Mode"
                        value={settings.attendance_mode || 'QR'}
                        onChange={(e) => setSettings({ ...settings, attendance_mode: e.target.value })}
                      >
                        <MenuItem value="QR">QR Code</MenuItem>
                        <MenuItem value="MANUAL">Manual / Register</MenuItem>
                        <MenuItem value="BIOMETRIC">Biometric</MenuItem>
                        <MenuItem value="HYBRID">Hybrid</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>QR Code Type</InputLabel>
                      <Select
                        label="QR Code Type"
                        value={settings.qr_type || 'STATIC'}
                        onChange={(e) => setSettings({ ...settings, qr_type: e.target.value })}
                      >
                        <MenuItem value="STATIC">Static QR</MenuItem>
                        <MenuItem value="DYNAMIC">Dynamic QR</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="QR Refresh Interval (Sec)"
                      value={settings.qr_refresh_interval ?? 0}
                      onChange={(e) => setSettings({ ...settings, qr_refresh_interval: Number(e.target.value) })}
                      helperText="0 for static QR, e.g. 30 for dynamic"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Working Hours (Per Day)"
                      inputProps={{ step: 0.5, min: 0, max: 24 }}
                      value={settings.working_hours ?? 8}
                      onChange={(e) => setSettings({ ...settings, working_hours: Number(e.target.value) })}
                      helperText="Standard daily hours required"
                    />
                  </Grid>
                </Grid>

                <Divider />

                {/* Time Rules Section */}
                <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Time Rules & Cut-offs
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="time"
                      label="Check-in Start Time"
                      slotProps={{ inputLabel: { shrink: true }, input: { step: 1 } }}
                      value={settings.checkin_start_time || '05:00:00'}
                      onChange={(e) => setSettings({ ...settings, checkin_start_time: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="time"
                      label="Late After"
                      slotProps={{ inputLabel: { shrink: true }, input: { step: 1 } }}
                      value={settings.late_after || '09:15:00'}
                      onChange={(e) => setSettings({ ...settings, late_after: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="time"
                      label="Half Day After"
                      slotProps={{ inputLabel: { shrink: true }, input: { step: 1 } }}
                      value={settings.half_day_after || '11:00:00'}
                      onChange={(e) => setSettings({ ...settings, half_day_after: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="time"
                      label="Checkout After"
                      slotProps={{ inputLabel: { shrink: true }, input: { step: 1 } }}
                      value={settings.checkout_after || '17:00:00'}
                      onChange={(e) => setSettings({ ...settings, checkout_after: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Automation Switches */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.is_active}
                      onChange={(e) => setSettings({ ...settings, is_active: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Enable Attendance System (Is Active)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.auto_mark_absent}
                      onChange={(e) => setSettings({ ...settings, auto_mark_absent: e.target.checked })}
                      color="warning"
                    />
                  }
                  label="Auto Mark Absent (Run job at end of day)"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Card: QR Code Preview & Secret */}
        <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex' }}>
          <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', flex: 1 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <QrCode size={20} />
                  <Typography variant="h6" fontWeight={600}>QR Code & Security</Typography>
                </Box>
              }
              subheader="Scan check-in QR or regenerate cryptographic secret"
            />
            <Divider />
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 3, p: 4 }}>
              {settings?.qr_secret ? (
                <>
                  <Box
                    id="qr-code-container"
                    sx={{
                      p: 2.5,
                      bgcolor: 'white',
                      borderRadius: 3,
                      border: 1,
                      borderColor: 'divider',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                      display: 'inline-block'
                    }}
                  >
                    {qrData?.qr_image ? (
                      <img
                        src={qrData.qr_image}
                        alt="Attendance QR Code"
                        style={{ width: 200, height: 200, display: 'block' }}
                      />
                    ) : (
                      <QRCode
                        value={JSON.stringify({
                          school_id: currentSchool?.id || settings.school_id,
                          type: settings.qr_type || "STATIC",
                          signature: qrData?.signature || settings.qr_secret
                        })}
                        size={200}
                        level="H"
                      />
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.default', px: 2, py: 0.75, borderRadius: 2, border: 1, borderColor: 'divider', maxWidth: '100%' }}>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontFamily: 'monospace', maxWidth: 200 }}>
                      Secret: {settings.qr_secret}
                    </Typography>
                    <Tooltip title="Copy Secret">
                      <IconButton size="small" onClick={handleCopySecret}>
                        {copied ? <Check size={14} color="green" /> : <Copy size={14} />}
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: 240 }}>
                    <Button
                      variant="contained"
                      startIcon={<Download size={18} />}
                      onClick={handleDownloadQR}
                      fullWidth
                    >
                      Download QR Code
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshCw size={18} />}
                      onClick={handleRegenerateQR}
                      disabled={loading}
                      color="secondary"
                      fullWidth
                    >
                      Regenerate QR Secret
                    </Button>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px dashed', borderColor: 'divider', borderRadius: 2, color: 'text.secondary'
                  }}
                >
                  No QR available
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
